import { Link, useRouterState } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Search, Sun, Moon, X, Menu, ChevronDown, ChevronRight, ArrowRight } from "lucide-react";
import { useTheme } from "./theme-provider";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { searchContent, fetchSettings } from "@/lib/api";
import { splitSlug } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const nav = [
  { to: "/", label: "Beranda" },
  { to: "/artikel", label: "Artikel" },
  {
    label: "Sastra",
    children: [
      { to: "/novel", label: "Novel" },
      { to: "/cerpen", label: "Cerpen" },
      { to: "/puisi", label: "Puisi" },
    ],
  },
  { to: "/kategori", label: "Kategori" },
];

export function SiteHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [q, setQ] = useState("");
  const { theme, setTheme } = useTheme();
  const path = useRouterState({ select: (s) => s.location.pathname });

  const { data: searchResults, isLoading } = useQuery({
    queryKey: ["search", q],
    queryFn: () => searchContent(q),
    enabled: q.trim().length > 0,
  });

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 8);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  const { data: settings } = useSuspenseQuery({
    queryKey: ["settings"],
    queryFn: fetchSettings,
  });

  const siteName = settings?.site_name || "Lihar Aksara";
  let siteFirst = "";
  let siteRest = "";

  if (siteName.includes(" ")) {
    const parts = siteName.split(" ");
    siteFirst = parts[0];
    siteRest = parts.slice(1).join(" ");
  } else if (siteName.toLowerCase().startsWith("lihar")) {
    siteFirst = siteName.substring(0, 5);
    siteRest = siteName.substring(5);
  } else {
    siteFirst = siteName;
  }

  const branding = (
    <div className="flex items-center group">
      <span className="font-logo text-2xl font-bold tracking-tight text-foreground">
        {siteFirst}
      </span>
      <span className="font-logo text-2xl font-bold tracking-tight text-accent">
        {siteRest ? (siteName.includes(" ") ? ` ${siteRest}` : siteRest) : ""}
      </span>
    </div>
  );

  return (
    <>
      <header
        className={`sticky top-0 z-40 w-full transition-all duration-500 ${
          scrolled
            ? "backdrop-blur-xl bg-background/90 border-b border-border shadow-sm"
            : "backdrop-blur-md bg-background/80 border-b border-transparent"
        }`}
      >
        <div className="container-editorial flex items-center justify-between h-16 md:h-20">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="md:hidden p-2 -ml-2 text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Buka Menu"
            >
              <Menu size={24} className=" " />
            </button>
            <Link to="/" className="flex items-center">
              {branding}
            </Link>
          </div>

          <nav className="hidden md:flex items-center gap-4">
            {nav.map((n) => {
              if (n.children) {
                const isActive = n.children.some(c => 
                  c.to === "/" ? path === "/" : path.startsWith(c.to.split("/$")[0])
                );
                return (
                  <DropdownMenu key={n.label}>
                    <DropdownMenuTrigger className={`flex items-center gap-1 px-4 py-2 text-sm font-medium transition-colors hover:text-foreground ${isActive ? "text-foreground" : "text-muted-foreground"}`}>
                      {n.label} <ChevronDown size={16} className="  opacity-50" />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-48 p-2 bg-background/95 backdrop-blur-xl border-border rounded-xl shadow-2xl">
                      {n.children.map((child) => (
                        <DropdownMenuItem key={child.label} asChild>
                          <Link 
                            to={child.to} 
                            params={child.params}
                            className="w-full px-3 py-2 text-sm rounded-lg hover:bg-muted transition-colors cursor-pointer"
                          >
                            {child.label}
                          </Link>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                );
              }

              const active = n.to === "/" ? path === "/" : path.startsWith(n.to as string);
              return (
                <Link
                  key={n.label}
                  to={n.to as any}
                  className={`px-4 py-2 text-sm font-medium transition-colors relative ${
                    active
                      ? "text-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {n.label}
                  {active && (
                    <span className="absolute left-4 right-4 -bottom-1 h-0.5 bg-accent rounded-full" />
                  )}
                </Link>
              );
            })}
          </nav>

          <div className="flex items-center gap-1">
            <button
              onClick={() => setSearchOpen(true)}
              className="p-2 rounded-full hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
              aria-label="Cari"
            >
              <Search size={20} className=" " />
            </button>
            <button
              onClick={() => setTheme(theme === "light" ? "dark" : "light")}
              className="p-2 rounded-full hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
              aria-label="Ganti Tema"
            >
              {theme === "light" ? <Moon size={20} className=" " /> : <Sun size={20} className=" " />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-[100] bg-background md:hidden animate-in fade-in duration-300">
          <div className="flex flex-col h-full">
            <div className="flex items-center justify-between px-6 h-16 border-b border-border">
              <Link to="/" onClick={() => setMobileMenuOpen(false)}>
                {branding}
              </Link>
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="p-2 text-muted-foreground"
                aria-label="Tutup Menu"
              >
                <X size={28} className=" " />
              </button>
            </div>
            <nav className="flex-1 px-6 py-8 space-y-6 overflow-y-auto">
              {nav.map((n) => {
                if (n.children) {
                  const isActive = n.children.some(c => 
                    c.to === "/" ? path === "/" : path.startsWith(c.to.split("/$")[0])
                  );
                  return (
                    <div key={n.label} className="space-y-4">
                      <p className={`text-xs font-bold uppercase tracking-widest ${isActive ? "text-accent" : "text-muted-foreground/50"}`}>
                        {n.label}
                      </p>
                      <div className="pl-4 space-y-4 border-l border-border/50">
                        {n.children.map((child) => (
                          <Link
                            key={child.label}
                            to={child.to as any}
                            params={child.params}
                            onClick={() => setMobileMenuOpen(false)}
                            className="block text-2xl font-serif tracking-tight text-muted-foreground hover:text-accent transition-colors"
                          >
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  );
                }

                const active = n.to === "/" ? path === "/" : path.startsWith(n.to as string);
                return (
                  <Link
                    key={n.label}
                    to={n.to as any}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`block text-3xl font-serif tracking-tight ${
                      active ? "text-accent font-bold" : "text-muted-foreground"
                    }`}
                  >
                    {n.label}
                  </Link>
                );
              })}
            </nav>
            <div className="p-10 border-t border-border bg-muted/30">
              {(settings?.social_instagram ||
                settings?.social_twitter ||
                settings?.whatsapp_number) && (
                <>
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground mb-4">
                    Ikuti Kami
                  </p>
                  <div className="flex gap-6 text-sm font-medium">
                    {settings?.social_instagram && (
                      <a
                        href={`https://instagram.com/${settings.social_instagram}`}
                        target="_blank"
                        rel="noreferrer"
                        className="hover:text-accent transition-colors"
                      >
                        Instagram
                      </a>
                    )}
                    {settings?.social_twitter && (
                      <a
                        href={`https://twitter.com/${settings.social_twitter}`}
                        target="_blank"
                        rel="noreferrer"
                        className="hover:text-accent transition-colors"
                      >
                        Twitter (X)
                      </a>
                    )}
                    {settings?.whatsapp_number && (
                      <a
                        href={`https://wa.me/${settings.whatsapp_number}`}
                        target="_blank"
                        rel="noreferrer"
                        className="hover:text-accent transition-colors"
                      >
                        WhatsApp
                      </a>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Search Overlay */}
      {searchOpen && (
        <div
          className="fixed inset-0 z-[100] bg-background/95 backdrop-blur-md animate-in fade-in duration-300"
          onClick={() => setSearchOpen(false)}
        >
          <div className="container-editorial pt-10 md:pt-24" onClick={(e) => e.stopPropagation()}>
            <div className="max-w-3xl mx-auto">
              <div className="flex items-center gap-4 mb-12 border-b-2 border-border pb-4 focus-within:border-accent transition-colors">
                <Search size={24} className="  text-muted-foreground" />
                <input
                  autoFocus
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Cari artikel, novel, atau kategori..."
                  className="flex-1 bg-transparent text-2xl md:text-4xl font-serif outline-none placeholder:text-muted-foreground/50"
                />
                <button
                  onClick={() => setSearchOpen(false)}
                  className="p-2 text-muted-foreground hover:text-foreground"
                  aria-label="Tutup Pencarian"
                >
                  <X size={32} className=" " />
                </button>
              </div>

              <div className="max-h-[60vh] overflow-y-auto pr-4 custom-scrollbar">
                {isLoading && (
                  <div className="flex items-center gap-3 text-muted-foreground animate-pulse">
                    <div className="w-2 h-2 rounded-full bg-accent animate-bounce" />
                    <span className="text-sm font-medium">Mencari...</span>
                  </div>
                )}

                {searchResults && searchResults.length > 0 && (
                  <div className="grid gap-8 py-8">
                    {searchResults.map((result: any) => (
                      <Link
                        key={result.id}
                        to={result.type === "ARTICLE" ? "/artikel/$id/$slug" : "/novel/$id/$slug"}
                        params={splitSlug(result.slug || "")}
                        onClick={() => setSearchOpen(false)}
                        className="group flex flex-col md:flex-row md:items-center justify-between gap-4 p-4 -m-4 rounded-2xl hover:bg-muted/50 transition-colors"
                      >
                        <div className="space-y-1">
                          <h4 className="text-xl font-serif font-bold group-hover:text-accent transition-colors">
                            {result.title}
                          </h4>
                          <p className="text-sm text-muted-foreground line-clamp-1">
                            {result.excerpt}
                          </p>
                        </div>
                        <div className="flex items-center gap-4 text-[10px] font-bold uppercase tracking-widest text-muted-foreground group-hover:text-accent transition-colors shrink-0">
                          <span>{result.type === "NOVEL" ? "Novel" : result.type === "SHORTSTORY" ? "Cerpen" : result.type === "POETRY" ? "Puisi" : "Artikel"}</span>
                          <ChevronRight size={12} className=" " />
                        </div>
                      </Link>
                    ))}
                  </div>
                )}

                {q.trim().length > 0 && !isLoading && (!searchResults || searchResults.length === 0) && (
                  <div className="py-20 text-center">
                    <p className="text-xl text-muted-foreground font-serif italic">
                      Tidak menemukan apa pun untuk "{q}"
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

